import React from 'react';
import t from '@jetshop/intl';
import { ElevateFacetAccordion } from './ElevateFacetAccordion';
import { useElevate } from '../ElevateContext';
import { TextFacet } from './types/TextFacet';
import { ColorFacet } from './types/ColorFacet';
import { SizeFacet } from './types/SizeFacet';
import { RangeFacet } from './types/RangeFacet';
import { ElevateSorting } from './ElevateSorting';
import { styled } from 'linaria/react';
import { ReactComponent as Cross26Icon } from '../../../svg/Cross26.svg';
import { elementPressAnimation } from '../../ui/animations/elementPressAnimation';
import { css, cx } from 'linaria';
import { ReactComponent as Cross26 } from '../../../svg/Cross26.svg';
import { CheckboxFacet } from './types/CheckboxFacet';

const stickyBackgroundStyle = css`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(6px);
`;
const FacetsFlyoutWrapper = styled('aside')`
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const FacetsFlyoutHeader = styled('div')`
  z-index: 2;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #f4f4f5;
  position: sticky;
  top: 0;

  h2 {
    font-size: 18px;
    font-weight: 700;
    line-height: 156%;
    text-align: center;
    width: 100%;
  }

  button {
    margin-right: -3px;
    background: transparent;
    width: 27px;
    height: 27px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 27px;
      height: 27px;
    }
  }
`;

const FacetsFlyoutHeadContent = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 24px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  /* Hide scrollbar but keep scrolling */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge legacy */
  &::-webkit-scrollbar {
    display: none; /* Safari/Chrome */
  }
`;

const FacetsFlyoutFooter = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.8);
  position: sticky;
  bottom: 0;
  box-shadow: 0 -4px 24px 0 rgba(0, 0, 0, 0.06);

  button {
    height: 50px;
    padding: 10px 16px;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 14px;
    line-height: 100%;
    padding-top: 12px;

    &.clear-all-button {
      background: #e6e6e6;
      color: #18181b;
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      &:disabled {
        opacity: 0.5;
      }

      svg {
        width: 24px;
        height: 24px;
      }
    }

    &.apply-button {
      flex: 2;
      background: #0d3923;
      color: #ffffff;
      box-shadow: 0 10px 15px -3px rgba(19, 78, 74, 0.2),
        0 4px 6px -4px rgba(19, 78, 74, 0.2);
    }
  }
`;

export const ElevateFacets = ({
  page,
  nrOfActiveFacets,
  facets,
  sort,
  totalHits,
  hideTarget,
  originalTerm
}) => {
  const { handleFacetClick, handleRangeFacetChanged, clearAllFacets } =
    useElevate();

  const renderFacet = facet => {
    const handleFacetClickCallback = (selectedValue, typeObject) => {
      handleFacetClick(
        typeObject.id,
        selectedValue.id,
        selectedValue.selected,
        originalTerm,
        typeObject.type
      );
    };

    const handleRangeFacetChangedCallback = ({ min, max, id }) => {
      handleRangeFacetChanged(min, max, id, originalTerm);
    };

    switch (facet.type) {
      case 'TEXT':
        return (
          <TextFacet {...facet} clickCallback={handleFacetClickCallback} />
        );

      case 'COLOR':
        return (
          <ColorFacet {...facet} clickCallback={handleFacetClickCallback} />
        );

      case 'SIZE':
        return (
          <SizeFacet {...facet} clickCallback={handleFacetClickCallback} />
        );

      case 'CHECKBOX':
        return (
          <CheckboxFacet
            {...facet}
            page={page}
            clickCallback={handleFacetClickCallback}
          />
        );

      case 'RANGE':
        return (
          <RangeFacet
            minValue={facet.min}
            maxValue={facet.max}
            {...facet}
            clickCallback={handleRangeFacetChangedCallback}
          />
        );

      default:
        return <></>;
    }
  };

  return (
    <FacetsFlyoutWrapper>
      <FacetsFlyoutHeader className={stickyBackgroundStyle}>
        <h2>{t('Filters and sorting')}</h2>
        <button onClick={hideTarget} className={elementPressAnimation}>
          <Cross26Icon />
        </button>
      </FacetsFlyoutHeader>
      <FacetsFlyoutHeadContent>
        {sort && <ElevateSorting sort={sort} />}

        {facets?.map(facet => (
          <ElevateFacetAccordion
            key={facet.id}
            title={facet.label}
            defaultOpen={false}
          >
            {renderFacet(facet)}
          </ElevateFacetAccordion>
        ))}
      </FacetsFlyoutHeadContent>
      <FacetsFlyoutFooter className={stickyBackgroundStyle}>
        <button
          className={cx(elementPressAnimation, 'clear-all-button')}
          onClick={clearAllFacets}
          disabled={!(nrOfActiveFacets > 0)}
        >
          {t('Clear')} <Cross26 />
        </button>
        <button
          className={cx(elementPressAnimation, 'apply-button')}
          onClick={hideTarget}
        >
          {t('Show products')}
          {nrOfActiveFacets > 0 && <span>&nbsp;({totalHits})</span>}
        </button>
      </FacetsFlyoutFooter>
    </FacetsFlyoutWrapper>
  );
};
